import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Heart, 
  Target, 
  Users, 
  Award,
  ArrowRight,
  Sparkles,
  Globe,
  Shield,
  HandHeart,
  Hammer,
  MapPin,
  Calendar,
  TrendingUp,
  CheckCircle,
  Star,
  Lightbulb,
  Gem,
  Eye,
  MessageSquare,
  Package,
  Rocket
} from 'lucide-react';

// Company values
const VALUES = [
  {
    icon: Gem,
    title: 'Autenticità',
    description: 'Ogni prodotto su Rarifex è genuinamente artigianale, realizzato in Italia da mani esperte. Niente produzione di massa, solo creazioni autentiche.',
    color: 'bg-amber-500'
  },
  {
    icon: Shield,
    title: 'Trasparenza',
    description: 'Prezzi chiari, comunicazione diretta tra cliente e artigiano, pagamenti protetti. Nessun costo nascosto, nessuna sorpresa.',
    color: 'bg-blue-500'
  },
  {
    icon: HandHeart,
    title: 'Sostenibilità',
    description: 'Supportiamo l\'economia locale e le tradizioni artigianali. Ogni acquisto è un investimento nel futuro del Made in Italy.',
    color: 'bg-green-500'
  },
  {
    icon: Users,
    title: 'Comunità',
    description: 'Non siamo solo un marketplace. Siamo una comunità di artigiani, appassionati e amanti del bello che credono nel valore del lavoro manuale.',
    color: 'bg-purple-500'
  }
];

// Mission pillars
const MISSION_PILLARS = [
  {
    title: 'Per gli Artigiani',
    description: 'Diamo agli artigiani italiani una vetrina digitale dove possono raccontare la loro storia, mostrare il loro lavoro e raggiungere clienti in tutto il mondo, senza intermediari.',
    icon: Hammer
  },
  {
    title: 'Per i Clienti',
    description: 'Offriamo ai clienti l\'opportunità di scoprire prodotti unici, conoscere chi li realizza e acquistare con la certezza di portare a casa un pezzo di eccellenza italiana.',
    icon: Heart
  },
  {
    title: 'Per il Made in Italy',
    description: 'Preserviamo e promuoviamo le tradizioni artigianali italiane, aiutando le botteghe a prosperare nell\'era digitale senza perdere la loro anima.',
    icon: MapPin
  }
];

// Vision Pillars (replaces fake history timeline)
const VISION_PILLARS = [
  {
    icon: Sparkles,
    title: 'La Visione',
    description: 'Creare il punto di riferimento per l\'artigianato italiano nel mondo digitale, dove qualità e autenticità vengono premiate.'
  },
  {
    icon: Target,
    title: 'La Missione',
    description: 'Connettere chi crea con passione a chi cerca unicità, eliminando le barriere tra bottega e cliente.'
  },
  {
    icon: Rocket,
    title: 'L\'Ambizione',
    description: 'Diventare la casa digitale di ogni artigiano italiano, preservando tradizioni secolari nell\'era moderna.'
  },
  {
    icon: Heart,
    title: 'La Promessa',
    description: 'Ogni prodotto su Rarifex è 100% Made in Italy, realizzato da mani esperte con materiali di qualità.'
  }
];

// Stats — solo dati verificabili, nessun numero inventato
const STATS = [
  { value: '100%', label: 'Made in Italy', icon: Award },
  { value: 'Zero', label: 'Intermediari', icon: Hammer },
  { value: '250+', label: 'Categorie artigianali', icon: Package },
  { value: 'Su misura', label: 'Creazioni personalizzabili', icon: MapPin }
];

// Why choose us
const WHY_CHOOSE = [
  {
    title: 'Artigiani Verificati',
    description: 'Ogni artigiano viene verificato prima di entrare nella piattaforma. Controlliamo identità, qualità del lavoro e autenticità della produzione.'
  },
  {
    title: 'Pagamenti Protetti',
    description: 'I tuoi pagamenti sono gestiti da Stripe e trattenuti fino alla conferma di ricezione. Zero rischi per te.'
  },
  {
    title: 'Comunicazione Diretta',
    description: 'Parli direttamente con chi realizza il tuo prodotto. Nessun intermediario, solo collaborazione autentica.'
  },
  {
    title: 'Prodotti Unici',
    description: 'Niente produzione industriale. Ogni pezzo è realizzato a mano con cura, attenzione e passione.'
  }
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Chi Siamo | Rarifex - Il Marketplace dell'Artigianato Italiano</title>
        <meta name="description" content="Scopri la storia di Rarifex, il marketplace che connette artigiani italiani e amanti del Made in Italy. La nostra missione: preservare e promuovere l'eccellenza artigianale italiana." />
        <meta name="keywords" content="chi siamo rarifex, storia rarifex, missione rarifex, artigianato italiano, made in italy, marketplace artigiani" />
        <link rel="canonical" href="https://www.rarifex.com/chi-siamo" />
        <meta property="og:title" content="Chi Siamo | Rarifex - Il Marketplace dell'Artigianato Italiano" />
        <meta property="og:description" content="La nostra missione: connettere artigiani italiani e amanti del bello, preservando le tradizioni del Made in Italy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/chi-siamo" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#008080]/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                La Nostra Storia
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Dove l'artigianato{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#008080] to-[#006666]">
                  incontra il digitale
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Rarifex nasce dalla passione per il Made in Italy e dalla volontà di dare 
                agli artigiani italiani la visibilità che meritano.
              </p>
            </div>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="container mx-auto px-4 -mt-4 mb-16">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&fit=crop"
                alt="Bottega artigiana italiana"
                className="w-full h-[300px] md:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <p className="text-white text-lg md:text-xl font-medium max-w-2xl">
                  "Crediamo che dietro ogni oggetto ci sia una storia che merita di essere raccontata, 
                  e mani esperte che meritano di essere celebrate."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MISSION */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Target className="w-4 h-4" />
                  La Nostra Missione
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Connettere chi crea con chi apprezza
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                  Rarifex è il ponte tra gli artigiani italiani e chi cerca prodotti autentici, 
                  unici e realizzati con passione. Non siamo solo un marketplace: siamo una 
                  comunità che celebra l'eccellenza del Made in Italy.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {MISSION_PILLARS.map((pillar, index) => (
                  <Card key={index} className="p-8 bg-gradient-to-br from-slate-50 to-white border-slate-200 hover:shadow-lg transition-all text-center">
                    <div className="w-16 h-16 bg-[#008080]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <pillar.icon className="w-8 h-8 text-[#008080]" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{pillar.title}</h3>
                    <p className="text-slate-600">{pillar.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  I nostri valori
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Questi sono i principi che guidano ogni nostra decisione.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {VALUES.map((value, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200 hover:border-[#008080]/30 hover:shadow-lg transition-all">
                    <div className={`w-12 h-12 ${value.color} rounded-xl flex items-center justify-center mb-4`}>
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-slate-600">{value.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* VISION PILLARS (was Story Timeline) */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 bg-[#008080]/20 text-[#00CED1] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Eye className="w-4 h-4" />
                  La Nostra Direzione
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Dove stiamo andando
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Rarifex nasce nel 2026 con un obiettivo chiaro: ridare valore all'artigianato italiano 
                  nell'era digitale, creando connessioni autentiche tra chi crea e chi apprezza.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {VISION_PILLARS.map((pillar, index) => (
                  <Card 
                    key={index} 
                    className="bg-slate-800/50 border-slate-700/50 p-6 hover:bg-slate-800/80 transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#008080]/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#008080]/30 transition-colors">
                        <pillar.icon className="w-6 h-6 text-[#00CED1]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                        <p className="text-slate-400 leading-relaxed">{pillar.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {/* Startup badge */}
              <div className="mt-12 text-center">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#008080]/20 to-amber-500/20 rounded-full px-6 py-3 border border-[#008080]/30">
                  <Rocket className="w-5 h-5 text-[#00CED1]" />
                  <span className="text-white font-medium">Lanciato nel 2026, orgogliosamente italiano</span>
                  <MapPin className="w-4 h-4 text-amber-400" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#008080] to-[#006666]">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {STATS.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-white/70">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    Perché scegliere Rarifex?
                  </h2>
                  <p className="text-lg text-slate-600 mb-8">
                    In un mondo di produzione di massa, Rarifex è un'oasi di autenticità. 
                    Ecco cosa ci rende diversi.
                  </p>
                  
                  <div className="space-y-6">
                    {WHY_CHOOSE.map((item, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-slate-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=600&fit=crop"
                    alt="Artigiano al lavoro"
                    className="rounded-2xl shadow-xl w-full"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 max-w-[200px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    </div>
                    <p className="text-sm text-slate-600">"Qualità eccezionale e servizio impeccabile"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MANIFESTO */}
        <section className="py-16 md:py-24 bg-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Lightbulb className="w-12 h-12 text-amber-500 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Il nostro manifesto
              </h2>
              
              <div className="prose prose-lg mx-auto text-slate-700">
                <p className="text-xl leading-relaxed mb-6">
                  Crediamo che l'artigianato non sia solo un mestiere, ma una forma d'arte. 
                  Ogni pezzo racconta una storia: la storia di chi lo ha creato, delle tecniche 
                  tramandate di generazione in generazione, dei materiali scelti con cura.
                </p>
                <p className="text-xl leading-relaxed mb-6">
                  In un'epoca di produzione industriale e oggetti identici, scegliamo di 
                  celebrare l'unicità. Scegliamo di dare voce a chi lavora con le mani, 
                  a chi dedica ore a perfezionare un dettaglio, a chi mette passione in ogni gesto.
                </p>
                <p className="text-xl leading-relaxed font-semibold text-slate-900">
                  Rarifex non è solo un marketplace. È un movimento per preservare l'eccellenza 
                  italiana e portarla nel futuro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* JOIN US / CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#DAA520] to-[#B8860B]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Fai parte della nostra storia
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Che tu sia un artigiano in cerca di visibilità o un amante del bello 
                in cerca di autenticità, c'è posto per te su Rarifex.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/register?role=crafter">
                  <Button size="lg" className="bg-white text-[#B8860B] hover:bg-slate-100">
                    <Hammer className="w-5 h-5 mr-2" />
                    Diventa Artigiano
                  </Button>
                </Link>
                <Link to="/esplora">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Package className="w-5 h-5 mr-2" />
                    Esplora i Prodotti
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT TEASER */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <MessageSquare className="w-10 h-10 text-[#008080] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Vuoi saperne di più?
              </h3>
              <p className="text-slate-600 mb-6">
                Siamo sempre felici di raccontare la nostra storia e rispondere alle tue domande.
              </p>
              <Link to="/contatti">
                <Button variant="outline" className="border-[#008080] text-[#008080] hover:bg-[#008080]/10">
                  Contattaci
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
